export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

export interface CacheConfig {
  maxSize?: number;

  defaultTtlMs?: number;
}

export interface UseCacheOptions {
  key?: string;

  ttl?: number;

  staleWhileRevalidate?: boolean;
}

class LRUCache {
  private readonly cache = new Map<string, CacheEntry<unknown>>();
  private readonly maxSize: number;
  private readonly defaultTtl: number;

  constructor(config?: CacheConfig) {
    this.maxSize = config?.maxSize ?? 150;
    this.defaultTtl = config?.defaultTtlMs ?? 5 * 60 * 1000;
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    this.cache.delete(key);
    this.cache.set(key, entry);

    return entry.data as T;
  }

  getStale<T>(key: string): { data: T; isStale: boolean } | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const isStale = Date.now() - entry.timestamp > entry.ttl;

    this.cache.delete(key);
    this.cache.set(key, entry);

    return { data: entry.data as T, isStale };
  }

  set<T>(key: string, data: T, ttl?: number): void {
    if (this.cache.size >= this.maxSize) {
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey !== undefined) {
        this.cache.delete(oldestKey);
      }
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttl ?? this.defaultTtl,
    });
  }

  has(key: string): boolean {
    return this.get(key) !== null;
  }

  invalidate(key: string): void {
    this.cache.delete(key);
  }

  invalidatePattern(pattern: RegExp | string): void {
    const regex = typeof pattern === 'string' ? new RegExp(pattern, 'i') : pattern;
    for (const key of this.cache.keys()) {
      if (regex.test(key)) {
        this.cache.delete(key);
      }
    }
  }

  clear(): void {
    this.cache.clear();
  }

  get size(): number {
    return this.cache.size;
  }
}

export const apiCache = new LRUCache({ maxSize: 150, defaultTtlMs: 5 * 60 * 1000 });

export const buildCacheKey = (method: string, endpoint: string, body?: unknown): string =>
  `${method.toUpperCase()}:${endpoint}:${body ? JSON.stringify(body) : ''}`;

export const isCacheableMethod = (method: string): boolean => method.toUpperCase() === 'GET';

export const getInvalidationPatterns = (method: string, endpoint: string): string[] => {
  const patterns: string[] = [];
  const upperMethod = method.toUpperCase();

  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(upperMethod)) {
    patterns.push(`GET:${endpoint}$`);

    const parts = endpoint.split('/').filter(Boolean);
    if (parts.length >= 2) {
      const parent = `/${parts.slice(0, -1).join('/')}`;
      patterns.push(`GET:${parent}`);
      patterns.push(`GET:${parent}/?`);
    }

    const resource = parts[0] || '';
    if (resource) {
      patterns.push(`GET:/${resource}$`);
      patterns.push(`GET:/${resource}/`);
    }
  }

  return patterns;
};
